/* eslint-disable @typescript-eslint/no-unused-vars */
import { createStore } from "vuex";
import api, { setAccessToken } from "@/api/index";
import router from "@/router";
import { jwtDecode } from "jwt-decode";
import { extractApiErrorMessage } from "@/utils/feedback";

type ProfileLike = { id?: string | number; [key: string]: any } | null;
let eventsRequestInFlight: Promise<any> | null = null;
let confirmResolver: ((value: boolean) => void) | null = null;

const hasCapabilityInProfile = (profile: any, capability: string): boolean =>
  Boolean(profile?.authorization?.capabilities?.[capability]);

export default createStore({
  state: {
    user: null,
    // Optimalizáció + TS: explicit típus, hogy a profile mező kulcsai (pl. id) elérhetők legyenek.
    profile: null as null | { id?: string | number; [key: string]: any },
    profileLoadedAt: 0,
    snackbar: {},
    alert: {},
    confirmDialog: {
      showing: false,
      title: "",
      text: "",
      confirmText: "Megerősítés",
      cancelText: "Mégse",
      color: "error",
      icon: "mdi-alert-circle-outline",
    },
    loggedIn: false,
    maps: [] as Array<{
      id: string;
      name: string;
      description: string;
      address: string;
      contactPage: string;
      imgUrl: string;
    }>,
    posts: [] as Array<{
      id: string;
      title: string;
      content: string;
      authorId?: string;
      createdAt?: string;
    }>,
    events: [] as Array<{
      id: string;
      name: string;
      description: string;
      date: string;
      mapId: string;
      endOfApply: string;
      maxParticipants: number;
      organizerId: string;
      archivedAt?: string | null;
      allowedGroupIds?: number[];
      allowedGroups?: Array<{ id: number; name: string }>;
      User?: { fullName: string; permissionId: number };
      Map?: {
        id: string;
        name: string;
        address: string;
        contactPage: string;
      };
      eventApplications?: Array<any>;
    }>,
    eventsLoadedAt: 0,
    loading: false,
    loadingStates: {} as Record<string, boolean>,
  },
  mutations: {
    SET_USER(state, user) {
      state.user = user;
    },
    SET_SNACKBAR(state, snackbar) {
      state.snackbar = snackbar;
    },
    SET_ALERT(state, alert) {
      state.alert = alert;
    },
    SET_CONFIRM_DIALOG(state, dialog) {
      state.confirmDialog = {
        ...state.confirmDialog,
        ...dialog,
      };
    },
    SET_LOGINSTATUS(state, loggedIn) {
      state.loggedIn = loggedIn;
    },
    SET_PROFILE(state, profile) {
      if (profile?.Permission && profile.Permission.power == null) {
        const permissionName = String(profile.Permission.name || "").trim();
        const localPowerByPermission: Record<string, number> = {
          Felhasználó: 10,
          "Játék szervező": 100,
          Fejlesztő: 500,
        };
        profile.Permission.power = Number(
          localPowerByPermission[permissionName] || 0
        );
      }
      state.profile = profile;
      // Optimalizáció: eltároljuk a profil frissességét, hogy ne kérjük le túl sűrűn a backendről.
      state.profileLoadedAt = profile ? Date.now() : 0;
    },
    setMaps(state, maps) {
      state.maps = maps;
    },
    setLoading(state, loading) {
      state.loading = loading;
    },
    SET_LOADING_STATE(state, payload: { key: string; value: boolean }) {
      state.loadingStates = {
        ...state.loadingStates,
        [payload.key]: payload.value,
      };
      state.loading = Object.values(state.loadingStates).some(Boolean);
    },
    ADD_MAP(state, map) {
      state.maps = state.maps.concat(map);
    },
    removeMap(state, mapId) {
      // Filterelés, hogy eltávolítjuk a kívánt 'id' szerint
      state.maps = state.maps.filter((map) => map.id !== mapId);
    },
    setEvents(state, events) {
      state.events = events;
      state.eventsLoadedAt = Array.isArray(events) ? Date.now() : 0;
    },
    ADD_EVENT(state, event) {
      state.events = state.events.concat(event);
    },
    removeEvent(state, eventId) {
      state.events = state.events.filter((event) => event.id !== eventId);
    },
    /**
     * Hozzáad egy új blogbejegyzést a posts tömbhöz.
     * @param {Object} state - A Vuex állapot
     * @param {Object} newPost - A létrehozott blogbejegyzés adatai
     */
    ADD_POST(state, newPost) {
      // Hozzáadjuk a tömb elejére, hogy azonnal látható legyen a listában
      state.posts.unshift(newPost);
    },
  },
  actions: {
    setSnackbar({ commit }, snackbar) {
      snackbar.showing = true;
      commit("SET_SNACKBAR", snackbar);
    },
    setAlert({ commit }, alert) {
      alert.showing = true;
      commit("SET_ALERT", alert);
    },
    confirmDialog({ commit }, dialog) {
      commit("SET_CONFIRM_DIALOG", {
        showing: true,
        title: dialog?.title || "Biztosan folytatod?",
        text: dialog?.text || "",
        confirmText: dialog?.confirmText || "Megerősítés",
        cancelText: dialog?.cancelText || "Mégse",
        color: dialog?.color || "error",
        icon: dialog?.icon || "mdi-alert-circle-outline",
      });
      return new Promise<boolean>((resolve) => {
        confirmResolver = resolve;
      });
    },
    resolveConfirmDialog({ commit }, confirmed: boolean) {
      commit("SET_CONFIRM_DIALOG", { showing: false });
      if (confirmResolver) {
        confirmResolver(Boolean(confirmed));
        confirmResolver = null;
      }
    },
    setLoadingState({ commit }, payload) {
      commit("SET_LOADING_STATE", payload);
    },
    setErrorSnackbar({ dispatch }, payload) {
      const text =
        typeof payload === "string"
          ? payload
          : extractApiErrorMessage(
              payload?.error ?? payload,
              payload?.fallback
            );
      dispatch("setSnackbar", {
        title: payload?.title || "Hiba",
        text,
        color: payload?.color || "error",
      });
    },
    async loginUser({ commit }, loginCredentials) {
      try {
        const payload = {
          identifier: String(loginCredentials?.identifier || "").trim(),
          password: String(loginCredentials?.password || "").trim(),
        };
        const userData = await api.post("/user/login", payload);

        const { responseData } = userData.data;
        const { accessToken, loggedIn, responseMessage } = responseData;

        commit("SET_LOGINSTATUS", loggedIn);

        // Token beállítás globálisan (axios interceptor stb.)
        setAccessToken(accessToken);
        // Optimalizáció: setAccessToken már ment localStorage-be, itt nem írjuk újra feleslegesen.

        // Felhasználói adatok kiolvasása a tokenből
        const decoded: any = jwtDecode(accessToken);
        commit("SET_USER", decoded);

        // Profil lekérése azonnal bejelentkezés után
        await this.dispatch("loadUserFromToken");

        if (responseMessage === "Sikeres bejelentkezés") {
          router.push({ name: "forum-page" }); // vagy bárhova akarod küldeni
        }

        return { responseMessage, loggedIn };
      } catch (error: any) {
        if (error.response) {
          const errorMessage = error.response.data.responseData.responseMessage;
          return { responseMessage: errorMessage, loggedIn: false }; // <- hibás login
        } else if (error.request) {
          return { responseMessage: "Hálózati hiba történt", loggedIn: false };
        } else {
          return {
            responseMessage: "Hiba történt a kérés feldolgozása során",
            loggedIn: false,
          };
        }
      }
    },
    async getProfileData({ commit, state }) {
      const token = localStorage.getItem("accessToken");
      if (token) {
        const profile = state.profile as ProfileLike;
        // Optimalizáció: ha friss a profil cache, azt használjuk extra API hívás helyett.
        if (profile?.id && Date.now() - state.profileLoadedAt < 60_000) {
          return profile;
        }

        try {
          const profileDataRequest = await api.get("/profile");
          const profileData = profileDataRequest.data.responseMessage;

          if (profileDataRequest.status != 200) {
            throw new Error(profileData.responseMessage);
          }

          commit("SET_PROFILE", profileData);
          return profileData; // Sikeres profiladatok visszaadása, ha szükséges
        } catch (error: any) {
          if (error.response) {
            // Ha van szerver válasz, de az hibás
            const errorMessage = error.response.data.responseData;
            router.push({ name: "login-page" });
            return {
              error: errorMessage.responseMessage,
              status: error.response.status,
            };
          } else if (error.request) {
            // Ha nincs válasz a szervertől
            return {
              error: "Hálózati hiba történt",
              status: error.response.status,
            };
          } else {
            // Axios konfigurációs hiba vagy más probléma
            return {
              error: "Hiba történt a kérés feldolgozása során",
              status: error.response.status,
            };
          }
        }
      }
    },
    async loadUserFromToken({ commit, state }, options?: { force?: boolean }) {
      const token = localStorage.getItem("accessToken");
      if (token) {
        const decoded: any = jwtDecode(token);
        commit("SET_USER", decoded);
        commit("SET_LOGINSTATUS", true);

        const force = options?.force === true;
        const profile = state.profile as ProfileLike;
        // Optimalizáció: route guard alatt rövid TTL-es cache-t használunk a backend terhelésének csökkentésére.
        if (
          !force &&
          profile?.id &&
          Date.now() - state.profileLoadedAt < 60_000
        ) {
          return profile;
        }

        try {
          const response = await api.get("/profile");
          const profileData = response.data.responseMessage;
          commit("SET_PROFILE", profileData);
          return profileData;
        } catch (err) {
          console.error("Profil betöltése sikertelen:", err);
          commit("SET_USER", null);
          commit("SET_LOGINSTATUS", false);
          commit("SET_PROFILE", null);
          return null;
        }
      }
      commit("SET_USER", null);
      commit("SET_LOGINSTATUS", false);
      commit("SET_PROFILE", null);
    },
    async deleteProfileData({ commit }) {
      try {
        const response = await api.post(
          "/profile/logout",
          {},
          {
            withCredentials: true, // FONTOS!
          }
        ); // A refreshToken elküldése a body-ban
        localStorage.removeItem("accessToken");
        return response.data;
        // ... további teendők (pl. tokenek törlése a frontendről, felhasználói állapot frissítése) ...
      } catch (error) {
        console.error("Hiba a kijelentkezéskor:", error);
        // ... hibakezelés ...
      }
    },
    async getPermissions({ commit }, highest?: boolean) {
      const token = localStorage.getItem("accessToken");
      if (token) {
        try {
          // const permissionsRequest = await api().get("/profile/permissions");
          const permissionsRequest = await api.get("/profile/permissions");
          const permissions = permissionsRequest.data.responseData.permissions;

          setAccessToken(token);

          if (highest) {
            // Keresd meg a legmagasabb power értékű permission-t
            const highestPermission = permissions.reduce(
              (max: any, permission: any) =>
                permission.power > max.power ? permission : max
            );
            return highestPermission.power;
          } else {
            // Visszaadja az összes permissions-t
            return permissions;
          }
        } catch (error: any) {
          if (error.response) {
            // Ha van szerver válasz, de az hibás
            const errorMessage =
              error.response.data.responseData.responseMessage;
            return errorMessage;
          }
        }
      }
    },
    async getGroups({ commit }) {
      const token = localStorage.getItem("accessToken");
      if (token) {
        try {
          // const groupsRequest = await api().get("/profile/groups");
          const groupsRequest = await api.get("/profile/groups");
          const groups = groupsRequest.data.responseData.groups;

          setAccessToken(token);

          return groups;
        } catch (error: any) {
          if (error.response) {
            // Ha van szerver válasz, de az hibás
            const errorMessage =
              error.response.data.responseData.responseMessage;
            return errorMessage;
          }
        }
      }
    },
    async getMailTemplates({ commit }) {
      const token = localStorage.getItem("accessToken");
      if (token) {
        try {
          // const mailTemplatesRequest = await api().get("/mail");
          const mailTemplatesRequest = await api.get("/mail");
          const mailTemplates =
            mailTemplatesRequest.data.responseData.templates;

          setAccessToken(token);

          return mailTemplates;
        } catch (error: any) {
          if (error.response) {
            // Ha van szerver válasz, de az hibás
            const errorMessage =
              error.response.data.responseData.responseMessage;
            return errorMessage;
          }
        }
      }
    },
    async sendForgottenPasswordEmail({ commit }, identifier: string) {
      try {
        const safeIdentifier = encodeURIComponent(
          String(identifier || "").trim()
        );
        const res = await api.post(`/profile/${safeIdentifier}/resetpassword`);
        return res; // teljes Axios response, benne status, data stb.
      } catch (error: any) {
        if (error.response) {
          return error.response; // így a komponensben ugyanúgy tudod kezelni
        } else if (error.request) {
          return {
            status: 0,
            data: {
              responseData: { responseMessage: "Hálózati hiba történt" },
            },
          };
        } else {
          return {
            status: 0,
            data: {
              responseData: {
                responseMessage: "Hiba történt a kérés feldolgozása során",
              },
            },
          };
        }
      }
    },
    async resetUserPassword({ commit }, userPayload) {
      try {
        const { identifier, password } = userPayload;
        const safeIdentifier = encodeURIComponent(
          String(identifier || "").trim()
        );
        const safePassword = String(password || "").trim();
        const resetPasswordRequest = await api.put(
          `/profile/${safeIdentifier}/resetpassword`,
          { password: safePassword }
        );

        const resetPassword =
          resetPasswordRequest.data.responseData.responseMessage;
        if (resetPasswordRequest.status == 200) {
          return resetPassword;
        }
      } catch (error: any) {
        if (error.response) {
          // Ha van szerver válasz, de az hibás
          const errorMessage = error.response.data.responseData.responseMessage;
          return errorMessage;
        }
      }
    },
    async fetchMaps({ commit }) {
      commit("SET_LOADING_STATE", { key: "maps", value: true });

      try {
        // API hívás a pályák lekérdezéséhez
        const response = await api.get("/maps");

        // A responseData.responseMessage az adatok tömbje
        const mapsData = response.data.responseData.responseMessage;

        // Ellenőrizd, hogy mapsData egy tömb
        if (Array.isArray(mapsData) && mapsData.length > 0) {
          // Ha a mapsData tömb tartalmaz adatokat
          const maps = mapsData.map((item: any) => ({
            id: item.id,
            name: item.name,
            address: item.address,
            description: item.description,
            contactPage: item.contactPage,
            imgUrl: item.imgUrl,
          }));

          // A maps tömböt közvetlenül commitoljuk a store-ba
          commit("setMaps", maps);
        } else {
          // Ha mapsData nem tömb, vagy üres tömb, akkor üres tömböt adunk
          commit("setMaps", []);
        }
      } catch (error: any) {
        console.error(
          "Hiba a pályák lekérdezésekor:",
          error.response?.data?.responseData?.responseMessage || error.message
        );

        // Ellenőrizd, hogy 500-as hibát kaptunk-e
        if (error.response?.status === 500) {
          throw new Error(
            extractApiErrorMessage(error, "Nem sikerült betölteni a pályákat.")
          );
        } else {
          throw new Error(
            extractApiErrorMessage(error, "Nem sikerült betölteni a pályákat.")
          );
        }
      } finally {
        commit("SET_LOADING_STATE", { key: "maps", value: false });
      }
    },
    async addMap({ commit }, newMapData) {
      try {
        const response = await api.post("/maps/new", newMapData);
        const newMap = response.data.responseMessage;
        commit("ADD_MAP", newMap);
      } catch (error: any) {
        let errorMessage = "Ismeretlen hiba történt.";

        if (error.response) {
          const res = error.response.data.responseData.responseMessage;

          if (Array.isArray(res)) {
            // Több hibaüzenet összefűzése egy stringgé
            errorMessage = res.map((e) => e.error).join("<br />");
          } else if (typeof res === "string") {
            errorMessage = res;
          }

          if (error.response.status === 500) {
            if (Array.isArray(res)) {
              // Több hibaüzenet összefűzése egy stringgé
              errorMessage = res.map((e) => e.error).join("<br />");
            } else if (typeof res === "string") {
              errorMessage = res;
            }
          }
        } else if (error.request) {
          errorMessage = "Hálózati hiba történt.";
        }

        // Elküldjük a hívónak a hibát
        throw new Error(errorMessage);
      } finally {
        commit("SET_LOADING_STATE", { key: "maps", value: false });
      }
    },
    async removeMap({ commit, state }, mapId) {
      try {
        // API hívás a pálya törléséhez
        const response = await api.delete(`/maps/remove/`, {
          data: { id: mapId },
        });

        if (response.status === 200) {
          // Ha sikerült a törlés, eltávolítjuk a pályát a Vuex-ből
          commit("removeMap", mapId);

          // Ha szükséges, frissíthetjük a pályák listáját
          // Például a törölt térképet eltávolíthatjuk a maps-ból helyben, így nem szükséges újra lekérni
          const updatedMaps = state.maps.filter((map) => map.id !== mapId);
          commit("setMaps", updatedMaps); // A frissített lista beállítása Vuex-ben
        }
      } catch (error) {
        console.error("Hiba a pálya törlésénél:", error);
      }
    },
    async addEvent({ commit }, newEventData) {
      try {
        const payload = {
          name: String(newEventData?.name || "").trim(),
          description: String(newEventData?.description || "").trim(),
          date: String(newEventData?.date || "").trim(),
          mapId: String(newEventData?.mapId || "").trim(),
          endOfApply: String(newEventData?.endOfApply || "").trim(),
          maxParticipants: Math.round(
            Number(newEventData?.maxParticipants || 0)
          ),
          allowedGroupIds: Array.isArray(newEventData?.allowedGroupIds)
            ? newEventData.allowedGroupIds
                .map((value: any) => Number(value))
                .filter((value: number) => Number.isInteger(value) && value > 0)
            : [],
          sendNotificationEmails: Boolean(newEventData?.sendNotificationEmails),
        };
        const response = await api.post("/events/new", payload);
        const newEvent = response.data.responseMessage;
        const message = response.data.responseData.responseMessage;
        commit("ADD_EVENT", newEvent);
        return message;
      } catch (error: any) {
        let errorMessage = "Ismeretlen hiba történt.";

        if (error.response) {
          const res = error.response.data.responseData.responseMessage;

          if (Array.isArray(res)) {
            // Több hibaüzenet összefűzése egy stringgé
            errorMessage = res.map((e) => e.error).join("<br />");
          } else if (typeof res === "string") {
            errorMessage = res;
          }

          if (error.response.status === 500) {
            if (Array.isArray(res)) {
              errorMessage = res.map((e) => e.error).join("<br />");
            } else if (typeof res === "string") {
              errorMessage = res;
            }
          }
        } else if (error.request) {
          errorMessage = "Hálózati hiba történt.";
        }

        // Elküldjük a hívónak a hibát
        throw new Error(errorMessage);
      } finally {
        commit("setLoading", false);
      }
    },
    async fetchEvents(
      { commit, state },
      options?: { force?: boolean; silent?: boolean }
    ) {
      const force = options?.force === true;
      const silent = options?.silent === true;
      const hasFreshCache =
        Array.isArray(state.events) &&
        state.events.length > 0 &&
        Date.now() - Number(state.eventsLoadedAt || 0) < 15_000;

      if (!force && hasFreshCache) {
        return state.events;
      }

      if (eventsRequestInFlight) {
        if (!force) {
          return eventsRequestInFlight;
        }
        // Force refresh must not reuse a potentially stale in-flight request.
        try {
          await eventsRequestInFlight;
        } catch {
          // Ignore previous request failure here; force refresh will run below.
        }
      }

      eventsRequestInFlight = (async () => {
        if (!silent) {
          commit("setLoading", true);
        }
        try {
          const response = await api.get("/events"); // Használjuk a beállított axios példányt
          const events = response.data.responseData.events;

          if (!Array.isArray(events))
            throw new Error("Nem megfelelő esemény adat");

          commit("setEvents", events);
          return events;
        } catch (error: any) {
          console.error(
            "Események betöltése sikertelen:",
            error?.response?.data?.responseData?.responseMessage ||
              error.message
          );
          throw error;
        } finally {
          eventsRequestInFlight = null;
          if (!silent) {
            commit("setLoading", false);
          }
        }
      })();

      return eventsRequestInFlight;
    },
    async removeEvent({ commit, state }, eventId) {
      try {
        // API hívás a pálya törléséhez
        const response = await api.delete(`/events/remove/`, {
          data: { eventId: eventId },
        });

        if (response.status === 200) {
          // Ha sikerült a törlés, eltávolítjuk a pályát a Vuex-ből
          commit("removeEvent", eventId);

          // Ha szükséges, frissíthetjük a pályák listáját
          // Például a törölt térképet eltávolíthatjuk a maps-ból helyben, így nem szükséges újra lekérni
          const updatedEvents = state.events.filter(
            (event: any) => event.id !== eventId
          );
          commit("setEvents", updatedEvents); // A frissített lista beállítása Vuex-ben
        }
      } catch (error) {
        console.error("Hiba a pálya törlésénél:", error);
      }
    },
    /**
     * Blogbejegyzés létrehozása (POST kérés a backend felé).
     * @param {Object} context - A Vuex action kontextusa ({ commit, dispatch, state })
     * @param {Object} payload - Az elküldendő adatok (title, content, cover_image, status, categories)
     * @returns {Promise<Object>} - A backend válaszát visszaadó Promise
     */
    async addPost({ commit }, payload) {
      try {
        const isFormData =
          typeof FormData !== "undefined" && payload instanceof FormData;
        const response = await api.post(
          "/blog/post/",
          payload,
          isFormData
            ? { headers: { "Content-Type": "multipart/form-data" } }
            : undefined
        );

        // A backend sikeresen válaszolt (201 status)
        const newPost = response.data.responseData.item;

        // Frissítjük a state-et a sikeresen létrehozott bejegyzéssel
        commit("ADD_POST", newPost);

        // Visszaadjuk az egész válasz objektumot, hogy a komponens lássa a 'responseData' struktúrát
        return response.data;
      } catch (error: any) {
        // Hiba történt (pl. 4xx vagy 5xx status kód)
        console.error("Hiba az addPost actionben:", error.response || error);

        // Fontos: Elutasítjuk a Promise-t, hogy a komponens (ahol a createPost fut)
        // a catch blokkba kerüljön, és kezelni tudja a hibát.
        return Promise.reject(error);
      }
    },
  },
  modules: {},
  getters: {
    maps: (state) => state.maps,
    loading: (state) => state.loading,
    events: (state) => state.events,
    isLoading: (state) => (key: string) => Boolean(state.loadingStates[key]),
    hasCapability: (state) => (capability: string) =>
      hasCapabilityInProfile(state.profile, capability),
  },
});
